import type {
  BrandConfig,
  BrandResearchSnapshot,
  BrandStrategy,
  CharacterArchetype,
  ColorEntry,
  DirectingRules,
  VoiceMapping,
} from "@/config/brand";
import { normalizeBrandTypography } from "@/config/brandTypography";

export interface BrandPromptContextOverrides {
  tagline?: string;
  tone?: string;
  description?: string;
  audience?: string;
  mission?: string;
  colorPalette?: ColorEntry[];
}

export interface BrandPromptContextOptions {
  includeTagline?: boolean;
  includeTone?: boolean;
  includeColors?: boolean;
  includeDescription?: boolean;
  includeAudience?: boolean;
  includeMission?: boolean;
  includeFidelity?: boolean;
  includeCompositing?: boolean;
  includeDirectingRules?: boolean;
  overrides?: BrandPromptContextOverrides;
}

export interface BuildCanonicalBrandConfigParams {
  brandId: string;
  timestamp: string;
  brandName: string;
  description: string;
  genre: string;
  styleReference?: string;
  visuals?: Partial<BrandConfig["visuals"]>;
  selectedLogo?: {
    aesthetic?: string;
    promptUsed?: string;
  };
  strategy?: Partial<BrandStrategy> | null;
  research?: Partial<BrandResearchSnapshot> | null;
  directingRules?: DirectingRules | null;
  characters?: CharacterArchetype[] | null;
  voiceMap?: VoiceMapping[] | null;
  worldBible?: string | null;
  icons?: Array<{ name: string; svgString: string }>;
  savedDeckAssets?: unknown;
}

export interface DraftBrandConfigCampaignData {
  brandCoAgentInput?: {
    brandName: string;
    description: string;
    genre: string;
    styleReference?: string;
    industry?: string;
    website?: string;
    competitors?: string[];
  };
  brandWebsiteContent?: string;
  brandQueryPlan?: Record<string, unknown> | null;
  brandTwitterResult?: BrandResearchSnapshot["twitter"];
  brandPerplexityResult?: BrandResearchSnapshot["perplexity"];
  brandGoogleSearchResult?: BrandResearchSnapshot["googleSearch"];
  brandYoutubeResult?: BrandResearchSnapshot["youtube"];
  brandSynthesis?: BrandResearchSnapshot["synthesis"];
  brandPopulateResult?: {
    visuals?: Partial<BrandConfig["visuals"]>;
    missionStatement?: string;
    /** @deprecated Only present on legacy pipeline results */
    toneOfVoice?: string;
    /** @deprecated Only present on legacy pipeline results */
    targetAudience?: string;
    directingRules?: DirectingRules;
    characters?: CharacterArchetype[];
    voiceMap?: VoiceMapping[];
    worldBible?: string;
  };
  brandSavedId?: string;
  brandLogoOptions?: Array<{
    imageData?: string;
    mimeType?: string;
    aesthetic?: string;
    label?: string;
    promptUsed?: string;
    svgData?: string;
  }>;
  brandLogoSelectedIndex?: number;
  brandIconResults?: Array<{ name: string; svgString: string }>;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function hasOwnValue(record: Record<string, unknown>): boolean {
  return Object.values(record).some((value) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  });
}

function normalizeStrategy(strategy?: Partial<BrandStrategy> | null): BrandStrategy | undefined {
  if (!strategy) return undefined;

  const next: BrandStrategy = {
    brandTagline: toNonEmptyString(strategy.brandTagline) ?? undefined,
    uniqueValueProposition: toNonEmptyString(strategy.uniqueValueProposition) ?? undefined,
    targetAudience: toNonEmptyString(strategy.targetAudience) ?? undefined,
    brandPersonality: toNonEmptyString(strategy.brandPersonality) ?? undefined,
    suggestedTone: toNonEmptyString(strategy.suggestedTone) ?? undefined,
    competitiveAdvantage: toNonEmptyString(strategy.competitiveAdvantage) ?? undefined,
    contentStrategy: toNonEmptyString(strategy.contentStrategy) ?? undefined,
    missionStatement: toNonEmptyString(strategy.missionStatement) ?? undefined,
    toneOfVoice: toNonEmptyString(strategy.toneOfVoice) ?? undefined,
  };

  return hasOwnValue(next as Record<string, unknown>) ? next : undefined;
}

function normalizeResearch(
  research?: Partial<BrandResearchSnapshot> | null,
): BrandResearchSnapshot | undefined {
  if (!research) return undefined;

  const next: BrandResearchSnapshot = {
    input: research.input,
    websiteContent: toNonEmptyString(research.websiteContent) ?? undefined,
    queryPlan: research.queryPlan,
    twitter: research.twitter ?? null,
    perplexity: research.perplexity ?? null,
    googleSearch: research.googleSearch ?? null,
    youtube: research.youtube ?? null,
    synthesis: research.synthesis ?? null,
    completedAt: toNonEmptyString(research.completedAt) ?? undefined,
    sourceSessionId: toNonEmptyString(research.sourceSessionId) ?? undefined,
  };

  return hasOwnValue(next as Record<string, unknown>) ? next : undefined;
}

export function getBrandTagline(
  brand: Pick<BrandConfig, "strategy" | "contextDictionary">,
): string | null {
  return (
    toNonEmptyString(brand.strategy?.brandTagline) ??
    // Legacy fallback for brands created before contextDictionary cleanup:
    toNonEmptyString(brand.contextDictionary?.brandTagline) ??
    toNonEmptyString(brand.contextDictionary?.tagline)
  );
}

export function getBrandTone(
  brand: Pick<BrandConfig, "strategy" | "contextDictionary">,
): string | null {
  return (
    toNonEmptyString(brand.strategy?.toneOfVoice) ??
    toNonEmptyString(brand.strategy?.suggestedTone) ??
    toNonEmptyString(brand.strategy?.brandPersonality) ??
    // Legacy fallback for brands created before contextDictionary cleanup:
    toNonEmptyString(brand.contextDictionary?.toneOfVoice)
  );
}

export function getBrandAudience(
  brand: Pick<BrandConfig, "strategy" | "contextDictionary">,
): string | null {
  return (
    toNonEmptyString(brand.strategy?.targetAudience) ??
    // Legacy fallback for brands created before contextDictionary cleanup:
    toNonEmptyString(brand.contextDictionary?.targetAudience)
  );
}

export function getBrandMissionStatement(
  brand: Pick<BrandConfig, "strategy" | "contextDictionary">,
): string | null {
  return (
    toNonEmptyString(brand.strategy?.missionStatement) ??
    // Legacy fallback for brands created before contextDictionary cleanup:
    toNonEmptyString(brand.contextDictionary?.missionStatement)
  );
}

export function getBrandIcons(
  brand: Pick<BrandConfig, "contextDictionary">,
): Array<{ name: string; svgString: string }> {
  const value = brand.contextDictionary?.brandIcons;
  return Array.isArray(value)
    ? value.filter(
        (item): item is { name: string; svgString: string } =>
          !!item &&
          typeof item === "object" &&
          typeof (item as { name?: unknown }).name === "string" &&
          typeof (item as { svgString?: unknown }).svgString === "string",
      )
    : [];
}

export function getSavedDeckAssets<T = unknown>(
  brand: Pick<BrandConfig, "contextDictionary">,
): T | undefined {
  return brand.contextDictionary?.savedDeckAssets as T | undefined;
}

export function buildBrandPromptContextBlock(
  brand: BrandConfig,
  options: BrandPromptContextOptions = {},
): string | null {
  const {
    includeTagline = true,
    includeTone = true,
    includeColors = true,
    includeDescription = false,
    includeAudience = false,
    includeMission = false,
    includeFidelity = true,
    includeCompositing = true,
    includeDirectingRules = true,
    overrides,
  } = options;

  const brandName = toNonEmptyString(brand.identity?.name) ?? "Brand";
  const tagline =
    overrides?.tagline !== undefined ? toNonEmptyString(overrides.tagline) : getBrandTagline(brand);
  const tone =
    overrides?.tone !== undefined ? toNonEmptyString(overrides.tone) : getBrandTone(brand);
  const description =
    overrides?.description !== undefined
      ? toNonEmptyString(overrides.description)
      : toNonEmptyString(brand.identity?.description);
  const audience =
    overrides?.audience !== undefined
      ? toNonEmptyString(overrides.audience)
      : getBrandAudience(brand);
  const mission =
    overrides?.mission !== undefined
      ? toNonEmptyString(overrides.mission)
      : getBrandMissionStatement(brand);
  const colors = overrides?.colorPalette ?? brand.visuals?.colorPalette ?? [];
  const topColors = colors
    .slice(0, 3)
    .map((color) => `${color.name}: ${color.hex}`)
    .join(", ");
  const genre = toNonEmptyString(brand.identity?.genre);
  const fidelity = toNonEmptyString(brand.visuals?.fidelityKeywords);
  const compositingRule = toNonEmptyString(brand.visuals?.compositingRule);

  const directingRules: string[] = [];
  if (brand.directingRules?.thirtyDegreeRule) {
    directingRules.push("Avoid repeating the same camera angle between adjacent shots");
  }
  if (brand.directingRules?.establishingShotProtocol) {
    directingRules.push("Open new locations with a clear establishing shot");
  }
  if (brand.directingRules?.sixLayerFramework) {
    directingRules.push(
      "Describe subject, framing, camera movement, lens, lighting, and pacing in each shot",
    );
  }
  directingRules.push(
    ...(brand.directingRules?.customRules ?? []).map((rule) => rule.trim()).filter(Boolean),
  );

  const lines = [
    `Brand: ${brandName}.`,
    includeTagline && tagline ? `Tagline: "${tagline}".` : null,
    includeTone && tone ? `Tone: ${tone}.` : null,
    includeColors && topColors ? `Color system: ${topColors}.` : null,
    includeDescription && (genre || description)
      ? `Context: ${[genre, description].filter(Boolean).join(" / ")}.`
      : null,
    includeAudience && audience ? `Audience: ${audience}.` : null,
    includeMission && mission ? `Mission: ${mission}.` : null,
    includeFidelity && fidelity ? `Visual fidelity: ${fidelity}.` : null,
  ].filter((line): line is string => Boolean(line));

  const sections: string[] = [];
  if (lines.length > 1) {
    sections.push(`Brand grounding:\n${lines.join("\n")}`);
  }

  if (includeCompositing && compositingRule) {
    sections.push(`Avoid:\n${compositingRule}.`);
  }

  if (includeDirectingRules && directingRules.length > 0) {
    sections.push(`Direction rules:\n${directingRules.join(". ")}.`);
  }

  return sections.length > 0 ? sections.join("\n\n") : null;
}

export function buildCanonicalBrandConfig(params: BuildCanonicalBrandConfigParams): BrandConfig {
  const strategy = normalizeStrategy(params.strategy);
  const research = normalizeResearch(params.research);
  const icons = params.icons ?? [];
  const typography = normalizeBrandTypography(params.visuals?.typography);

  return {
    identity: {
      id: params.brandId,
      name: params.brandName,
      description: params.description,
      genre: params.genre,
      styleReference: params.styleReference ?? "",
      createdAt: params.timestamp,
      updatedAt: params.timestamp,
    },
    assets:
      params.selectedLogo &&
      (toNonEmptyString(params.selectedLogo.aesthetic) ||
        toNonEmptyString(params.selectedLogo.promptUsed))
        ? {
            "logo.aesthetic": params.selectedLogo.aesthetic ?? "",
            "logo.promptUsed": params.selectedLogo.promptUsed ?? "",
          }
        : {},
    visuals: {
      fidelityKeywords: "",
      compositingRule: "",
      colorPalette: [],
      aspectRatio: "16:9",
      typography,
      ...params.visuals,
    },
    directingRules: params.directingRules ?? {
      thirtyDegreeRule: false,
      establishingShotProtocol: false,
      sixLayerFramework: false,
      customRules: [],
    },
    characters: params.characters ?? [],
    voiceMap: params.voiceMap ?? [],
    worldBible: params.worldBible ?? "",
    strategy,
    research,
    contextDictionary: {
      brandIcons: icons.map((icon) => ({ name: icon.name, svgString: icon.svgString })),
      savedDeckAssets: params.savedDeckAssets ?? undefined,
    },
  };
}

export function buildDraftBrandConfigFromCampaignData(
  campaignData: DraftBrandConfigCampaignData,
  sessionId?: string | null,
): BrandConfig | undefined {
  const input = campaignData.brandCoAgentInput;
  const populate = campaignData.brandPopulateResult;
  if (!input || !populate?.visuals) return undefined;

  const selectedLogo =
    campaignData.brandLogoSelectedIndex !== undefined
      ? campaignData.brandLogoOptions?.[campaignData.brandLogoSelectedIndex]
      : campaignData.brandLogoOptions?.[0];

  return buildCanonicalBrandConfig({
    brandId: campaignData.brandSavedId || slugify(input.brandName) || "brand-preview",
    timestamp: new Date().toISOString(),
    brandName: input.brandName,
    description: input.description,
    genre: input.genre,
    styleReference: input.styleReference,
    visuals: populate.visuals,
    selectedLogo,
    strategy: {
      ...campaignData.brandSynthesis,
      missionStatement: populate.missionStatement,
      // toneOfVoice comes from synthesis.suggestedTone (rich 2-3 sentence version)
      toneOfVoice: campaignData.brandSynthesis?.suggestedTone || undefined,
      // targetAudience stays from synthesis spread (rich 2-3 sentence version)
    },
    research: {
      input: {
        brandName: input.brandName,
        description: input.description,
        genre: input.genre,
        styleReference: input.styleReference,
        industry: input.industry,
        website: input.website,
        competitors: input.competitors,
      },
      websiteContent: campaignData.brandWebsiteContent,
      queryPlan: campaignData.brandQueryPlan ?? null,
      twitter: campaignData.brandTwitterResult ?? null,
      perplexity: campaignData.brandPerplexityResult ?? null,
      googleSearch: campaignData.brandGoogleSearchResult ?? null,
      youtube: campaignData.brandYoutubeResult ?? null,
      synthesis: campaignData.brandSynthesis ?? null,
      sourceSessionId: sessionId ?? undefined,
      completedAt: new Date().toISOString(),
    },
    directingRules: populate.directingRules,
    characters: populate.characters,
    voiceMap: populate.voiceMap,
    worldBible: populate.worldBible,
    icons: (campaignData.brandIconResults ?? []).map((icon) => ({
      name: icon.name,
      svgString: icon.svgString,
    })),
  });
}
